// Disputes management — list, investigate, resolve
import { useState, useCallback, useMemo } from "react";
import { Scale, AlertCircle, UserCheck, Store } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDisputes,
  useResolveDispute,
  useInvestigateDispute,
} from "@/hooks/admin/usePayments";
import type { Dispute, DisputeStatus } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// DB-truth dispute statuses from migration 066. Color/label tables keyed by
// the real enum values only; merchant vs. customer resolution is derived from
// the separate `resolution` column on each row.
const STATUS_COLORS: Record<DisputeStatus, string> = {
  open: "bg-red-100 text-red-800 border-red-200",
  under_review: "bg-amber-100 text-amber-800 border-amber-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-stone-100 text-stone-700 border-stone-200",
};

const STATUS_LABELS: Record<DisputeStatus, string> = {
  open: "Ouvert",
  under_review: "En investigation",
  resolved: "Résolu",
  rejected: "Rejeté",
};

// The merchant/customer outcome is stored separately on `resolution`. Render a
// combined label like "Résolu — Commerçant" for resolved disputes, and the
// plain status label otherwise. Unknown statuses fall back to the raw value so
// the UI never shows a blank badge.
function formatDisputeLabel(
  status: string | null | undefined,
  resolution: "merchant" | "customer" | null | undefined,
): string {
  if (!status) return "—";
  const base = STATUS_LABELS[status as DisputeStatus] ?? status;
  if (status !== "resolved" || !resolution) return base;
  const resolutionLabel =
    resolution === "merchant" ? "Commerçant" : "Client";
  return `${base} — ${resolutionLabel}`;
}

function getDisputeBadgeClass(status: string | null | undefined): string {
  if (!status) return "bg-stone-100 text-stone-600 border-stone-200";
  return (
    STATUS_COLORS[status as DisputeStatus] ??
    "bg-stone-100 text-stone-600 border-stone-200"
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Resolution modal
// ---------------------------------------------------------------------------

interface ResolveModalProps {
  readonly open: boolean;
  readonly dispute: Dispute | null;
  readonly onClose: () => void;
  readonly onResolve: (resolution: "merchant" | "customer") => void;
  readonly isResolving: boolean;
}

function ResolveModal({
  open,
  dispute,
  onClose,
  onResolve,
  isResolving,
}: ResolveModalProps) {
  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Résoudre le litige"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isResolving}>
            Annuler
          </Button>
          <Button
            onClick={() => onResolve("merchant")}
            disabled={isResolving}
            className="gap-1.5"
          >
            <Store className="h-4 w-4" />
            Commerçant
          </Button>
          <Button
            onClick={() => onResolve("customer")}
            disabled={isResolving}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
          >
            <UserCheck className="h-4 w-4" />
            Client
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Commande{" "}
          <span className="font-mono font-semibold text-stone-900">
            {dispute?.order_ref}
          </span>
        </p>
        <p className="text-sm text-stone-600">
          Montant :{" "}
          <span className="font-semibold text-stone-900">
            {dispute ? formatFcfa(dispute.amount_fcfa) : ""}
          </span>
        </p>
        {dispute?.reason && (
          <p className="rounded-md bg-stone-50 px-3 py-2 text-sm text-stone-600">
            {dispute.reason}
          </p>
        )}
        <p className="text-sm font-medium text-stone-700">
          En faveur de qui souhaitez-vous résoudre ce litige ?
        </p>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Disputes() {
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | "">("");
  const [resolvingDispute, setResolvingDispute] = useState<Dispute | null>(null);

  const params = useMemo(
    () => ({ status: statusFilter || null }),
    [statusFilter],
  );

  const { data, isLoading, isError, refetch } = useDisputes(params);
  const resolveDispute = useResolveDispute();
  const investigateDispute = useInvestigateDispute();

  const disputes = data?.disputes ?? [];

  const openCount = useMemo(
    () => disputes.filter((d) => d.status === "open").length,
    [disputes],
  );

  const investigatingCount = useMemo(
    () => disputes.filter((d) => d.status === "under_review").length,
    [disputes],
  );

  const resolvedCount = useMemo(
    () => disputes.filter((d) => d.status === "resolved").length,
    [disputes],
  );

  const totalAmountDisputed = useMemo(
    () =>
      disputes
        .filter((d) => d.status === "open" || d.status === "under_review")
        .reduce((sum, d) => sum + d.amount_fcfa, 0),
    [disputes],
  );

  // Transition `open` → `under_review`. Previously this was incorrectly
  // wired to `resolveDispute.mutate(..., { resolution: "merchant" })` which
  // jumped straight to "resolved in merchant's favor" — never actually
  // moved disputes into the investigation state.
  const handleInvestigate = useCallback(
    (dispute: Dispute) => {
      investigateDispute.mutate(dispute.id);
    },
    [investigateDispute],
  );

  const handleResolve = useCallback(
    (resolution: "merchant" | "customer") => {
      if (!resolvingDispute) return;
      resolveDispute.mutate(
        { id: resolvingDispute.id, resolution },
        {
          onSuccess: () => setResolvingDispute(null),
        },
      );
    },
    [resolvingDispute, resolveDispute],
  );

  const columns: readonly ColumnDef<Dispute>[] = useMemo(
    () => [
      {
        id: "order_ref",
        header: "Commande",
        cell: (d) => (
          <span className="font-mono text-xs font-medium text-stone-900">
            {d.order_ref}
          </span>
        ),
      },
      {
        id: "amount_fcfa",
        header: "Montant",
        cell: (d) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(d.amount_fcfa)}
          </span>
        ),
      },
      {
        id: "reason",
        header: "Raison",
        cell: (d) => (
          <span className="max-w-xs truncate text-sm text-stone-600">
            {d.reason}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (d) => (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getDisputeBadgeClass(d.status)}`}
          >
            {formatDisputeLabel(d.status, d.resolution)}
          </span>
        ),
      },
      {
        id: "opened_at",
        header: "Ouvert le",
        cell: (d) => (
          <span className="text-sm text-stone-500">{formatDate(d.opened_at)}</span>
        ),
      },
      {
        id: "resolved_at",
        header: "Résolu le",
        cell: (d) => (
          <span className="text-sm text-stone-500">
            {d.resolved_at ? formatDate(d.resolved_at) : "—"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (d) => (
          <div className="flex items-center gap-1">
            {d.status === "open" && (
              <button
                type="button"
                onClick={() => handleInvestigate(d)}
                title="Passer en investigation"
                className="rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
              >
                Investiguer
              </button>
            )}
            {(d.status === "open" || d.status === "under_review") && (
              <button
                type="button"
                onClick={() => setResolvingDispute(d)}
                title="Résoudre le litige"
                className="rounded px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100"
              >
                Résoudre
              </button>
            )}
          </div>
        ),
      },
    ],
    [handleInvestigate],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Litiges
            </h1>
            <p className="text-sm text-stone-500">
              {isLoading ? "Chargement…" : `${disputes.length} litige(s)`}
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Litiges ouverts"
            value={openCount}
            format="number"
            icon={<Scale className="h-4 w-4" />}
          />
          <KPICard
            label="En investigation"
            value={investigatingCount}
            format="number"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Résolus ce mois"
            value={resolvedCount}
            format="number"
            icon={<UserCheck className="h-4 w-4" />}
          />
          <KPICard
            label="Montant en litige"
            value={totalAmountDisputed}
            format="currency"
            icon={<Scale className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DisputeStatus | "")}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="open">Ouvert</option>
            <option value="under_review">En investigation</option>
            <option value="resolved">Résolu</option>
            <option value="rejected">Rejeté</option>
          </select>
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les litiges.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && disputes.length === 0 ? (
              <EmptyState
                icon={<Scale className="h-6 w-6" />}
                title="Aucun litige"
                description="Les litiges de paiement apparaîtront ici."
              />
            ) : (
              <AdminDataTable
                data={disputes}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      {/* Resolution modal */}
      <ResolveModal
        open={resolvingDispute !== null}
        dispute={resolvingDispute}
        onClose={() => setResolvingDispute(null)}
        onResolve={handleResolve}
        isResolving={resolveDispute.isPending}
      />
    </AdminLayout>
  );
}
